import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import { FormikProps, getIn } from "formik";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { FormField } from "../FormField";
import { FixedLabel } from "../Functions";

export interface MtcFormFieldProps {
  field: FormField;
  formik: FormikProps<Record<string, unknown>>;
  variant: "filled" | "outlined" | "standard";
  classes: Record<string, string>;
}

export function DatePickerField({
  field,
  formik,
  variant,
  classes,
}: MtcFormFieldProps) {
  // getIn, not formik.values[field.id]: inside a FieldArray the id is a PATH
  // ("stateLicenses[0].expirationDate"), and a flat lookup on a nested values object returns
  // undefined — so every date picker in a field array rendered permanently blank and could not hold
  // a value. That included the licence expiry on the external provider's own profile.
  const value = getIn(formik.values, field.id) as Dayjs | null;
  const hasValue =
    value !== null && value !== undefined && (value as unknown) !== "";
  const error =
    getIn(formik.touched, field.id) && Boolean(getIn(formik.errors, field.id));
  const helperText = getIn(formik.touched, field.id)
    ? (getIn(formik.errors, field.id) as string)
    : undefined;

  return (
    <Grid size={{ xs: 12, sm: field.columns || 6 }} key={field.id}>
      {field.fixedLabel && <FixedLabel formik={formik} field={field} />}
      <Box sx={{ display: "flex" }}>
        <Box sx={{ flexGrow: 1 }}>
          <DatePicker
            label={!field.fixedLabel ? field.text : undefined}
            value={value}
            onChange={(value) => {
              formik.setFieldTouched(field.id, true, false);
              if (value && field.transform) {
                formik.setFieldValue(field.id, field.transform(value));
              } else {
                formik.setFieldValue(field.id, value);
              }
            }}
            slotProps={{
              textField: {
                // id/name: without them the input is anonymous in the DOM, so the submit-time
                // "jump to the first problem" lookup had nothing to find and a required date could
                // not be scrolled to or focused.
                id: field.id,
                name: field.id,
                fullWidth: true,
                variant,
                error,
                helperText,
                className: classes.formControl,
                sx: {
                  "& .MuiInputBase-input": {
                    height: "1.4375em",
                    padding: "16.5px 14px",
                  },
                },
              },
              field: {
                clearable: field.showClearButton && hasValue,
                onClear: () => formik.setFieldValue(field.id, null),
              },
            }}
          />
        </Box>
      </Box>
    </Grid>
  );
}

export function DateTimePickerField({
  field,
  formik,
  variant,
  classes,
}: MtcFormFieldProps) {
  // See DatePickerField: a flat lookup breaks this field inside a FieldArray.
  const value = getIn(formik.values, field.id) as Dayjs | null;
  const hasValue =
    value !== null && value !== undefined && (value as unknown) !== "";
  const error =
    getIn(formik.touched, field.id) && Boolean(getIn(formik.errors, field.id));
  const helperText = getIn(formik.touched, field.id)
    ? (getIn(formik.errors, field.id) as string)
    : undefined;

  return (
    <Grid size={{ xs: 12, sm: field.columns || 6 }} key={field.id}>
      {field.fixedLabel && <FixedLabel formik={formik} field={field} />}
      <Box sx={{ display: "flex" }}>
        <Box sx={{ flexGrow: 1 }}>
          <DateTimePicker
            label={!field.fixedLabel ? field.text : undefined}
            value={value}
            onChange={(value) => {
              formik.setFieldTouched(field.id, true, false);
              if (value && field.transform) {
                formik.setFieldValue(field.id, field.transform(value));
              } else {
                formik.setFieldValue(field.id, value);
              }
            }}
            slotProps={{
              textField: {
                // id/name: without them the input is anonymous in the DOM, so the submit-time
                // "jump to the first problem" lookup had nothing to find and a required date could
                // not be scrolled to or focused.
                id: field.id,
                name: field.id,
                fullWidth: true,
                variant,
                error,
                helperText,
                className: classes.formControl,
                sx: {
                  "& .MuiInputBase-input": {
                    height: "1.4375em",
                    padding: "16.5px 14px",
                  },
                },
              },
              field: {
                clearable: field.showClearButton && hasValue,
                onClear: () => formik.setFieldValue(field.id, null),
              },
            }}
          />
        </Box>
      </Box>
    </Grid>
  );
}
